import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { UserService } from '../providers/user.service';
import { RolePermissionService } from '../providers/role-permission.service';
import { JwtService } from '../providers/jwt.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private router: Router,
    private rolePermission: RolePermissionService,
    private jwtService: JwtService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const targetUrl = state.url;

    if (this.isLoginRoute(targetUrl)) {
      return this.handleLoginRoute();
    }

    const token = this.jwtService.getToken();
    const refreshToken = localStorage.getItem('vivent_jwt_refresh_token');

    if (token) {
      return this.proceedWithUserCheck(state);
    }

    if (refreshToken) {
      return this.jwtService.refreshToken(refreshToken).pipe(
        switchMap(newToken => {
          const expireTime = new Date().getTime() + newToken.expires_in * 1000;
          const curUsername = this.jwtService.getUsername();
          this.jwtService.saveToken(newToken.access_token, newToken.refresh_token, expireTime, curUsername);
          return this.proceedWithUserCheck(state);
        }),
        catchError(() => {
          this.jwtService.destroyToken();
          this.userService.logout();
          return of(this.router.createUrlTree(['/login']));
        })
      );
    }

    this.jwtService.destroyToken();
    this.userService.logout();
    return of(this.router.createUrlTree(['/login']));
  }

   private handleLoginRoute(): Observable<boolean | UrlTree> {
    const token = this.jwtService.getToken();

    if (!token) {
      this.jwtService.destroyToken();
      this.userService.logout();
      return of(true);
    }

    return of(this.router.createUrlTree(['/dashboard']));
  }

  private proceedWithUserCheck(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.userService.getUserRoleFromApi().pipe(
      map(role => {
        if (!role?.userRole) {
          return this.router.createUrlTree(['/login']);
        }

        const userRole = role.userRole.id;
        this.rolePermission.setCurrentRole(userRole);

        const targetUrl = state.url;
        const baseRoute = this.getBaseRoute(targetUrl);

        if (!this.rolePermission.isRouteAllowed('/' + baseRoute)) {
          return this.router.createUrlTree(['/dashboard']);
        }

        return true;
      }),
      catchError(() => of(this.router.createUrlTree(['/login'])))
    );
  }

  private getBaseRoute(url: string): string {
    return url.split('/')[1] || '';
  }

  private isLoginRoute(url: string): boolean {
    return ['/login', 'login'].includes(url);
  }
}
